// src/components/admin/business/PackageTable.jsx
import React from 'react';
import { formatCurrency } from '../../../utils/formatters';
import Pagination from '../../common/Pagination';
import Loading from '../../common/Loading';
import { FaEdit, FaTrash, FaStar, FaRegStar, FaBox, FaCog, FaBullhorn, FaUsers } from 'react-icons/fa';
import Swal from 'sweetalert2';

const PackageTable = ({
  packages = [],
  onEdit,
  onDelete,
  onToggleFeatured,
  onStatusChange,
  loading = false,
  currentPage = 1,
  totalPages = 1,
  onPageChange
}) => {
  const safePackages = Array.isArray(packages) ? packages : [];

  const getTypeColor = (type) => {
    const colors = {
      full_management: 'bg-purple-100 text-purple-800 border-purple-200',
      marketing: 'bg-blue-100 text-blue-800 border-blue-200',
      operational: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[type] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTypeIcon = (type) => {
    const icons = {
      full_management: <FaCog className="text-purple-600" />,
      marketing: <FaBullhorn className="text-blue-600" />,
      operational: <FaUsers className="text-green-600" />
    };
    return icons[type] || <FaBox className="text-gray-600" />;
  };

  const getTypeLabel = (type) => {
    const labels = {
      full_management: 'Full Management',
      marketing: 'Marketing & Booking',
      operational: 'Operational'
    };
    return labels[type] || type;
  };

  const formatPrice = (pkg) => {
    try {
      if (pkg.fixed_price_idr && parseFloat(pkg.fixed_price_idr) > 0) {
        return formatCurrency(pkg.fixed_price_idr);
      } else if (pkg.management_fee_percentage && parseFloat(pkg.management_fee_percentage) > 0) {
        return `${pkg.management_fee_percentage}% Management Fee`;
      } else {
        return 'Contact for Pricing';
      }
    } catch (error) {
      console.error('Error formatting price:', error, pkg);
      return 'Contact Us';
    }
  };

  // Handle delete with confirmation - WITH SUCCESS POPUP
  const handleDeleteClick = async (pkg) => {
    try {
      const result = await Swal.fire({
        title: 'Delete Package?',
        html: `Are you sure you want to delete <strong>"${pkg.name}"</strong>?<br><br><small class="text-gray-600">This action cannot be undone.</small>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Delete',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold',
          cancelButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });

      if (result.isConfirmed) {
        // Show loading
        Swal.fire({
          title: 'Deleting...',
          html: 'Please wait a moment',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Call parent delete
        await onDelete(pkg.id, pkg.name);
        
        // Show success popup
        await Swal.fire({
          title: 'Deleted!',
          html: `<strong>"${pkg.name}"</strong> has been successfully deleted.`,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'rounded-2xl',
            title: 'text-xl font-bold',
            confirmButton: 'px-6 py-3 rounded-xl font-semibold'
          }
        });
      }
    } catch (error) {
      console.error('Error in delete process:', error);
      Swal.close();
      
      await Swal.fire({
        title: 'Error',
        text: 'Failed to delete package. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    }
  };

  // Handle featured toggle - WITH SUCCESS POPUP
  const handleFeatureToggle = async (pkg) => {
    try {
      const isCurrentlyFeatured = pkg.featured;
      const action = isCurrentlyFeatured ? 'remove from' : 'add to';
      
      const result = await Swal.fire({
        title: 'Featured Status',
        html: `Are you sure you want to ${action} featured for <strong>"${pkg.name}"</strong>?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, Continue',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold',
          cancelButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });

      if (result.isConfirmed) {
        // Show loading
        Swal.fire({
          title: 'Updating...',
          html: 'Please wait a moment',
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });

        // Call parent function
        await onToggleFeatured(pkg.id, isCurrentlyFeatured, pkg.name);
        
        // Show success popup
        const successMessage = isCurrentlyFeatured 
          ? `<strong>"${pkg.name}"</strong> removed from featured list` 
          : `<strong>"${pkg.name}"</strong> added to featured list`;
        
        await Swal.fire({
          title: 'Success!',
          html: successMessage,
          icon: 'success',
          confirmButtonColor: '#10b981',
          confirmButtonText: 'OK',
          timer: 3000,
          timerProgressBar: true,
          customClass: {
            popup: 'rounded-2xl',
            title: 'text-xl font-bold',
            confirmButton: 'px-6 py-3 rounded-xl font-semibold'
          }
        });
      }
    } catch (error) {
      console.error('Error toggling featured:', error);
      Swal.close();
      
      await Swal.fire({
        title: 'Error',
        text: 'Failed to change featured status. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    }
  };

  // Handle status change - WITH SUCCESS POPUP
  const handleStatusChange = async (pkg, newStatus) => {
    try {
      // Show loading
      Swal.fire({
        title: 'Updating Status...',
        html: 'Please wait a moment',
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      // Call parent function
      await onStatusChange(pkg.id, newStatus, pkg.name);
      
      // Show success popup
      const statusText = newStatus === 'active' ? 'Active' : 'Inactive';
      await Swal.fire({
        title: 'Status Updated!',
        html: `<strong>"${pkg.name}"</strong> status changed to <strong>${statusText}</strong>`,
        icon: 'success',
        confirmButtonColor: '#10b981',
        confirmButtonText: 'OK',
        timer: 3000,
        timerProgressBar: true,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    } catch (error) {
      console.error('Error changing status:', error);
      Swal.close();
      
      await Swal.fire({
        title: 'Error',
        text: 'Failed to change package status. Please try again.',
        icon: 'error',
        confirmButtonColor: '#ef4444',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-xl font-bold',
          confirmButton: 'px-6 py-3 rounded-xl font-semibold'
        }
      });
    }
  };

  const getRowNumber = (index) => {
    const itemsPerPage = 10;
    return ((currentPage - 1) * itemsPerPage) + index + 1;
  };

  if (loading) return <Loading />;

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden">
      {/* Summary Info */}
      <div className="px-6 py-4 bg-gradient-to-r from-amber-50 via-amber-50/50 to-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <FaBox className="text-amber-600" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-gray-900">Total Packages</h3>
              <p className="text-xs text-gray-600">Showing {safePackages.length} package(s)</p>
            </div>
          </div>
          {totalPages > 0 && (
            <div className="text-sm font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gradient-to-r from-amber-50 via-amber-50/50 to-gray-50">
            <tr>
              <th className="px-4 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider w-16">
                No
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Package
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Pricing
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Features
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {safePackages.map((pkg, index) => {
              const rowNumber = getRowNumber(index);
              
              return (
                <tr key={pkg.id} className="hover:bg-amber-50/30 transition-colors duration-150">
                  <td className="px-4 py-4 text-center">
                    <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 text-amber-800 font-bold text-sm shadow-sm">
                      {rowNumber}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-16 w-16">
                        <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center shadow-sm border border-gray-200">
                          {getTypeIcon(pkg.package_type)}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-semibold text-gray-900">
                          {pkg.name || 'Unnamed Package'}
                        </div>
                        <div className="text-sm text-gray-600">
                          {pkg.short_description?.substring(0, 60) || pkg.description?.substring(0, 60) || 'No description'}
                          {(pkg.short_description?.length > 60 || pkg.description?.length > 60) ? '...' : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(pkg.package_type)}`}>
                      {getTypeLabel(pkg.package_type)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-bold text-gray-900">{formatPrice(pkg)}</div>
                    {pkg.support_fee && parseFloat(pkg.support_fee) > 0 && (
                      <div className="text-xs text-blue-600 font-medium">
                        + {formatCurrency(pkg.support_fee)} support
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="max-w-xs space-y-1 text-sm text-gray-700">
                      {pkg.features && Array.isArray(pkg.features) && pkg.features.length > 0 ? (
                        <>
                          {pkg.features.slice(0, 2).map((feature, idx) => (
                            <div key={idx} className="truncate text-xs text-gray-600">• {feature}</div>
                          ))}
                          {pkg.features.length > 2 && (
                            <div className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              +{pkg.features.length - 2} more
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400">No features listed</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-2">
                      <select
                        value={pkg.status || 'active'}
                        onChange={(e) => handleStatusChange(pkg, e.target.value)}
                        className="text-xs font-medium border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white transition-all shadow-sm hover:border-amber-400"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                      {pkg.featured && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 border border-amber-200 shadow-sm">
                          <FaStar className="mr-1.5" />
                          Featured
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      {/* Edit Button */}
                      <button
                        onClick={() => onEdit(pkg)}
                        className="inline-flex items-center justify-center p-2.5 border border-amber-300 shadow-sm text-sm font-semibold rounded-lg text-amber-700 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-all duration-200 transform hover:scale-110 active:scale-95"
                        title="Edit Package"
                      >
                        <FaEdit className="h-4 w-4" />
                      </button>

                      {/* Featured Toggle Button */}
                      <button
                        onClick={() => handleFeatureToggle(pkg)}
                        className={`inline-flex items-center justify-center p-2.5 border shadow-sm text-sm font-semibold rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-200 transform hover:scale-110 active:scale-95 ${
                          pkg.featured
                            ? 'border-yellow-400 text-yellow-700 bg-yellow-50 hover:bg-yellow-100 hover:border-yellow-500 focus:ring-yellow-500'
                            : 'border-gray-300 text-gray-700 bg-gray-50 hover:bg-gray-100 hover:border-gray-400 focus:ring-gray-500'
                        }`}
                        title={pkg.featured ? 'Remove from Featured' : 'Add to Featured'}
                      >
                        {pkg.featured ? (
                          <FaStar className="h-4 w-4" />
                        ) : (
                          <FaRegStar className="h-4 w-4" />
                        )}
                      </button>

                      {/* Delete Button */}
                      <button
                        onClick={() => handleDeleteClick(pkg)}
                        className="inline-flex items-center justify-center p-2.5 border border-red-300 shadow-sm text-sm font-semibold rounded-lg text-red-700 bg-red-50 hover:bg-red-100 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 transform hover:scale-110 active:scale-95"
                        title="Delete Package"
                      >
                        <FaTrash className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {safePackages.length === 0 && (
        <div className="text-center py-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 mb-4">
            <FaBox className="h-8 w-8 text-amber-600" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">No Packages Found</h3>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            Start by adding your first business package to manage your service offerings.
          </p>
        </div>
      )}

      {safePackages.length > 0 && (
        <div className="px-6 py-5 border-t border-gray-200 bg-gradient-to-r from-gray-50 via-amber-50/20 to-gray-50">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages || 1}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default PackageTable;